// Persetujuan — the approval inbox.
//
// Two audiences on one screen, which is deliberate: the person waiting on a
// decision and the person making it are usually the same shift, and splitting
// them into two pages means the cashier cannot see that their request was
// rejected an hour ago.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Clock, ShieldCheck, Ban, Inbox } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingState } from '@/components/ui/LoadingState';
import {
  useApprovalRequests,
  useApproveRequest,
  useRejectRequest,
  useCancelRequest,
} from '@/hooks/useApprovals';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { formatDateTime, formatIDR, cn } from '@/lib/format';
import type { ApprovalRequest, ApprovalStatus } from '@/lib/api/approvals';

const STATUS_CHIP: Record<
  ApprovalStatus,
  { label: string; variant: 'success' | 'warning' | 'danger' | 'default' }
> = {
  pending: { label: 'Menunggu', variant: 'warning' },
  approved: { label: 'Disetujui', variant: 'success' },
  rejected: { label: 'Ditolak', variant: 'danger' },
  cancelled: { label: 'Dibatalkan', variant: 'default' },
};

const KIND_LABEL: Record<string, string> = {
  'sale.tempo': 'Penjualan tempo',
  'sale.discount': 'Diskon di atas batas',
  'return.create': 'Retur barang',
  'transaction.void': 'Void transaksi',
  'customer.edit': 'Ubah data customer',
};

const CUSTOMER_FIELD: Record<string, string> = {
  name: 'Nama',
  phone: 'Telepon',
  company: 'Perusahaan',
  email: 'Email',
  address: 'Alamat',
  tax_id: 'NIK / NPWP',
};

/**
 * What the approver needs to see for the kinds that carry structure. Anything
 * else falls back to the generic amount/customer line in the card.
 */
function PayloadDetails({ req }: { req: ApprovalRequest }) {
  const p = req.payload;
  if (req.kind === 'transaction.void') {
    const txId = typeof p.transaction_id === 'string' ? p.transaction_id : null;
    const invoice = typeof p.invoice_number === 'string' ? p.invoice_number : null;
    const amount = typeof p.amount === 'number' ? p.amount : null;
    const customer = typeof p.customer === 'string' ? p.customer : null;
    return (
      <p className="text-small text-ink mt-2 tabular-nums">
        {txId ? (
          <Link to={`/invoice/${txId}`} className="font-mono text-primary hover:underline">
            {invoice ?? 'Lihat invoice'}
          </Link>
        ) : (
          invoice
        )}
        {amount !== null && ` · ${formatIDR(amount)}`}
        {customer && ` · ${customer}`}
      </p>
    );
  }
  if (req.kind === 'customer.edit') {
    const customerId = typeof p.customer_id === 'string' ? p.customer_id : null;
    const customer = typeof p.customer === 'string' ? p.customer : null;
    const changes =
      p.changes && typeof p.changes === 'object'
        ? (p.changes as Record<string, { from?: unknown; to?: unknown }>)
        : {};
    return (
      <div className="mt-2 text-small">
        {customerId ? (
          <Link to={`/customers/${customerId}`} className="text-primary hover:underline">
            {customer ?? 'Lihat customer'}
          </Link>
        ) : (
          <span className="text-ink">{customer}</span>
        )}
        <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          {Object.entries(changes).map(([field, ch]) => (
            <div key={field} className="contents">
              <dt className="text-caption text-ink-subtle">{CUSTOMER_FIELD[field] ?? field}</dt>
              <dd className="text-ink">
                <span className="line-through text-ink-subtle">{String(ch?.from ?? '—')}</span>
                {' → '}
                <span className="font-medium">{String(ch?.to ?? '—')}</span>
              </dd>
            </div>
          ))}
        </dl>
      </div>
    );
  }
  return null;
}

export default function Approvals() {
  const { user } = useAuth();
  const { approvalLevel, isApprover } = usePermissions();
  const [filter, setFilter] = useState<ApprovalStatus | 'all'>('pending');

  const { data: requests, isLoading } = useApprovalRequests(
    filter === 'all' ? undefined : filter,
  );
  const approve = useApproveRequest();
  const reject = useRejectRequest();
  const cancel = useCancelRequest();
  const [error, setError] = useState<string | null>(null);

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <>
      <PageHeader
        overline="Persetujuan"
        title="Approval"
        description={
          isApprover
            ? `Anda approver level ${approvalLevel}. Permintaan di bawah level Anda bisa disetujui langsung.`
            : 'Permintaan persetujuan yang Anda ajukan.'
        }
      />

      <div className="mb-4 inline-flex rounded border border-line bg-surface p-0.5">
        {(
          [
            { id: 'pending', label: 'Menunggu' },
            { id: 'approved', label: 'Disetujui' },
            { id: 'rejected', label: 'Ditolak' },
            { id: 'all', label: 'Semua' },
          ] as const
        ).map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => setFilter(opt.id)}
            className={cn(
              'px-3 py-1.5 text-caption font-medium rounded transition-colors',
              filter === opt.id ? 'bg-primary text-white' : 'text-ink-subtle hover:text-ink',
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded border border-danger/30 bg-danger/5 text-caption text-danger">
          {error}
        </div>
      )}

      {isLoading ? (
        <LoadingState message="Memuat permintaan…" />
      ) : !requests || requests.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Inbox size={24} strokeWidth={1.5} />}
            title="Tidak ada permintaan"
            description={
              filter === 'pending'
                ? 'Belum ada yang menunggu persetujuan.'
                : 'Tidak ada permintaan dengan status ini.'
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <RequestCard
              key={req.id}
              req={req}
              // You cannot sign your own slip — the database refuses it too, and
              // hiding the buttons saves the person finding that out by being
              // told off. Same for a rung you do not stand above.
              canAct={
                isApprover &&
                req.status === 'pending' &&
                req.requestedBy !== user?.uid &&
                approvalLevel > req.currentLevel
              }
              canCancel={req.status === 'pending' && req.requestedBy === user?.uid}
              pending={approve.isPending || reject.isPending || cancel.isPending}
              onApprove={(note) => act(() => approve.mutateAsync({ id: req.id, note }))}
              onReject={(note) => act(() => reject.mutateAsync({ id: req.id, note }))}
              onCancel={() => act(() => cancel.mutateAsync({ id: req.id }))}
            />
          ))}
        </div>
      )}
    </>
  );
}

function RequestCard({
  req,
  canAct,
  canCancel,
  pending,
  onApprove,
  onReject,
  onCancel,
}: {
  req: ApprovalRequest;
  canAct: boolean;
  canCancel: boolean;
  pending: boolean;
  onApprove: (note: string) => void;
  onReject: (note: string) => void;
  onCancel: () => void;
}) {
  const [note, setNote] = useState('');
  const chip = STATUS_CHIP[req.status];

  // The payload is whatever the requester attached. Kinds with structure get
  // their own block; the rest show the two fields worth a decision if present.
  const structured = req.kind === 'transaction.void' || req.kind === 'customer.edit';
  const amount = !structured && typeof req.payload.amount === 'number' ? req.payload.amount : null;
  const customer =
    !structured && typeof req.payload.customer === 'string' ? req.payload.customer : null;

  return (
    <Card padding="md">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-body font-display font-semibold text-ink">
              {KIND_LABEL[req.kind] ?? req.kind}
            </p>
            <Chip variant={chip.variant}>{chip.label}</Chip>
            <span className="text-caption text-ink-subtle inline-flex items-center gap-1">
              <ShieldCheck size={11} />
              Butuh L{req.requiredLevel}
              {req.currentLevel > 0 && ` · sudah L${req.currentLevel}`}
            </span>
            {req.consumedAt && (
              <span className="text-caption text-ink-subtle">· sudah dipakai</span>
            )}
          </div>
          <p className="text-caption text-ink-subtle mt-1">
            {req.requestedByName ?? 'Seseorang'} · {formatDateTime(req.createdAt)}
          </p>
          <PayloadDetails req={req} />
          {(amount !== null || customer) && (
            <p className="text-small text-ink mt-2 tabular-nums">
              {amount !== null && formatIDR(amount)}
              {amount !== null && customer && ' · '}
              {customer}
            </p>
          )}
          {req.reason && (
            <p className="text-small text-ink-subtle mt-1 italic">“{req.reason}”</p>
          )}
        </div>

        {req.status === 'pending' && (
          <div className="flex items-center gap-2 shrink-0">
            {canCancel && (
              <Button variant="ghost" leftIcon={<Ban size={14} />} disabled={pending} onClick={onCancel}>
                Batalkan
              </Button>
            )}
            {canAct && (
              <>
                <Button
                  variant="ghost"
                  leftIcon={<XCircle size={14} />}
                  disabled={pending}
                  onClick={() => onReject(note)}
                >
                  Tolak
                </Button>
                <Button
                  variant="primary"
                  leftIcon={<CheckCircle2 size={14} />}
                  disabled={pending}
                  onClick={() => onApprove(note)}
                >
                  Setujui
                </Button>
              </>
            )}
            {!canAct && !canCancel && (
              <span className="text-caption text-ink-subtle inline-flex items-center gap-1">
                <Clock size={12} />
                Menunggu approver lain
              </span>
            )}
          </div>
        )}
      </div>

      {canAct && (
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Catatan (opsional) — akan tersimpan di riwayat persetujuan"
          className="mt-3 w-full px-3 py-2 rounded border border-line bg-canvas text-small text-ink placeholder:text-ink-subtle focus:outline-none focus:border-primary"
        />
      )}
    </Card>
  );
}
